import React from "react";
import { LeadCard, Sec, NextPointer, InlineAdd } from "./parts";
import { onboardingView, wonView } from "./leadModel.mjs";
import { feeLines, feeLabel, agreementLink } from "../moneyModel.mjs";
import ClientBrief from "./ClientBrief";

/*
 * NEW CLIENT: the Closed won status, the fee, the onboarding checklist (frames 1a
 * and 1b, Pluto the morning after the win). The situation lasts until the welcome
 * and the finance intro are both done, or 30 days after the win
 * (record/situation.mjs).
 *
 *   - STATUS: "Closed won Sep 30 · 22:08 · set by you", the last thing they wrote
 *     before it ("Shahar wrote “done” at 21:50."), and "No signed copy is on record."
 *     while none is. Never "no PandaDoc email has arrived": nothing can prove an
 *     absence (reality.txt [1]). "Add agreement link" pastes the signed copy's
 *     link, which is what clears "no signed copy yet" everywhere at once.
 *   - THE FEE, the hero: signed_fee_structure, one line per part, labelled "Fee ·
 *     from the final agreement · not signed yet" until a signed copy is on record and
 *     "Signed fee" after (moneyModel.feeLabel). Empty, it says "not recorded" with its
 *     Add here, because Deal and money points at this block for the fee.
 *     "Final version Sep 29, after Gali’s redlines" is not drawn: nothing stores it.
 *   - ONBOARDING: the real rows are receipts ("Welcome sent Oct 1 · 09:42 · Cc
 *     Lukasz, Zoli", the Cc read off the send since PR #155) or point at their Next
 *     line ("owed since Sep 30 · in Next ↑"); kickoff, access, auditor, live and the
 *     account manager are inert Proposed rows. The count is the real rows only
 *     ("1 of 2 done", reality.txt [25]), and all done it collapses to one line.
 *   - THE CLIENT BRIEF (ClientBrief.js): written and posted to Slack on its own after the win; Open in Slack.
 *
 * Props:
 *   record   the shell's bundle: company (won_at, status_history,
 *            signed_fee_structure, signed_agreement_url, agreement_notice), act
 *            (onboarding, document, actions, facts), timeline.rows, zones
 *   ops      ops.patchCompany(body) (the fee and the agreement link)
 */
export default function NewClient({ record, ops }) {
  const co = record.company || {};
  const won = wonView({ company: co, act: record.act, rows: record.timeline.rows, zones: record.zones, now: record.now });
  const ob = onboardingView({ company: co, act: record.act, rows: record.timeline.rows, zones: record.zones, now: record.now });
  const fee = feeLines(co.signed_fee_structure);
  const link = agreementLink(co, record.act);

  return (
    <LeadCard label="New client" meta={ob.until}>
      <div>
        <div className="rec-strong">{won.headline}</div>
        <div className="rec-body">{[won.wrote, won.signedLine].filter(Boolean).join(" ")}</div>
        <div className="rec-lead__links">
          {link && link.kind === "document" ? (
            <a href={link.url} target="_blank" rel="noopener noreferrer">{link.label}</a>
          ) : (
            <>
              {link && (
                <a href={link.url} target="_blank" rel="noopener noreferrer">{link.label}</a>
              )}
              <InlineAdd label="Add agreement link" placeholder="The signed agreement’s link" onSave={(url) => ops.patchCompany({ signed_agreement_url: url })} />
            </>
          )}
        </div>
      </div>

      <Sec className="rec-lead__sec--12">
        <span className="rec-lead__small">{feeLabel(co, record.act)}</span>
        {fee.length ? (
          <>
            <div className="rec-hero">{fee[0]}</div>
            {fee.length > 1 && (
              <div className="rec-lead__lines">
                {fee.slice(1).map((l) => (
                  <span key={l}>{l}</span>
                ))}
              </div>
            )}
          </>
        ) : (
          <span className="rec-sub">
            not recorded ·{" "}
            <InlineAdd
              multiline
              placeholder={"$3,500/mo base\n15% of spend $10K–$50K"}
              hint="One line per part, as the agreement words it."
              onSave={(text) => ops.patchCompany({ signed_fee_structure: text })}
            />
          </span>
        )}
      </Sec>

      <Sec className="rec-lead__sec--12">
        {ob.collapsed ? (
          <span className="rec-lead__ink">
            <b>Onboarding</b> · {ob.rows.map((r) => r.text).join(" · ")}
          </span>
        ) : (
          <>
            <div className="rec-card__head">
              <span className="rec-strong">Onboarding</span>
              <span className="rec-meta">
                {ob.done} of {ob.total} done
              </span>
            </div>
            <div className="rec-checklist">
              {ob.rows.map((r) => (
                <div key={r.key} className={`rec-checkrow${r.done ? "" : " rec-checkrow--owed"}`}>
                  <span className={`rec-check${r.done ? "" : " rec-check--open"}`} aria-hidden="true">{r.done ? "✓" : ""}</span>
                  <span>
                    {r.text}
                    {r.cc && <span className="rec-lead__aside"> · {r.cc}</span>}
                    {!r.done && r.pointer && (
                      <>
                        {" "}
                        <NextPointer />
                      </>
                    )}
                  </span>
                  <span />
                </div>
              ))}
              {ob.proposed.map((p) => (
                <div key={p.key} className="rec-checkrow">
                  <span className="rec-check rec-check--proposed" aria-hidden="true" />
                  <span>
                    {p.text}
                    {p.sub && <span className="rec-lead__aside"> · {p.sub}</span>}
                    {p.add && <span className="rec-inert rec-lead__aside"> {p.add}</span>}
                  </span>
                  <span className="rec-proposed">Proposed</span>
                </div>
              ))}
            </div>
          </>
        )}
      </Sec>

      <ClientBrief companyId={co.id} />
    </LeadCard>
  );
}
